import { useCallback, useMemo } from 'react';

import { InventoryListingStatus } from '@ekhodealer/ekho-common';

import type { IInventoryItem } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';

import { usePublishInventoryMutation } from '../api/usePublishInventory';

/**
 * Categorized inventory items for publish operation
 */
export interface CategorizedPublishItems {
	/** Items ready to publish (status is DRAFT) */
	ready: IInventoryItem[];
	/** Items already published (status is PUBLISHED) */
	alreadyPublished: IInventoryItem[];
	/** Items not eligible to publish (status is INCOMPLETE_DRAFT, HIDDEN, or ARCHIVED) */
	notEligible: IInventoryItem[];
}

/**
 * Categorize items into ready, alreadyPublished, and notEligible groups
 */
const categorizePublishItems = (items: IInventoryItem[]): CategorizedPublishItems => {
	const result: CategorizedPublishItems = {
		ready: [],
		alreadyPublished: [],
		notEligible: [],
	};

	for (const item of items) {
		const status = item.listing?.status || InventoryListingStatus.DRAFT;

		if (status === InventoryListingStatus.DRAFT) {
			result.ready.push(item);
		} else if (status === InventoryListingStatus.PUBLISHED) {
			result.alreadyPublished.push(item);
		} else {
			// INCOMPLETE_DRAFT, HIDDEN, or ARCHIVED
			result.notEligible.push(item);
		}
	}

	return result;
};

/**
 * Hook for managing publish operations on selected inventory items
 * Provides execution using query hooks
 */
export const usePublishSelectedInventory = () => {
	const publishMutation = usePublishInventoryMutation();
	const currentServicesProviderId = useCurrentServicesProviderId();

	/**
	 * Execute publish for items
	 * Categorizes and publishes ready items
	 * Returns the count of items that were published
	 */
	const publishItemsByIds = useCallback(
		async (itemIds: Set<string>, items: IInventoryItem[]): Promise<number> => {
			if (!currentServicesProviderId || itemIds.size === 0 || items.length === 0) {
				return 0;
			}

			// Categorize items
			const categorized = categorizePublishItems(items);
			const readyItems = categorized.ready;

			if (readyItems.length === 0) {
				return 0;
			}

			const inventoryIds = readyItems.map((item) => item.id);

			await publishMutation.mutateAsync({
				servicesProviderId: currentServicesProviderId,
				inventoryIds,
			});

			return readyItems.length;
		},
		[publishMutation, currentServicesProviderId],
	);

	return useMemo(
		() => ({
			publishItemsByIds,
			isPending: publishMutation.isPending,
		}),
		[publishItemsByIds, publishMutation.isPending],
	);
};
